import { Cached } from '@mui/icons-material';
import { Stack, Box, IconButton, Grid, Tooltip } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { Cell } from 'react-table';
import { deleteJobBoardAccounts, IJobBoardDTO } from 'wisestep-api';
import { Label, WsDataTable, WsIconDelete, WsIconEdit } from 'wisestep-base';

import { useWorkspaceContext } from '../../../../context';
import {
  useNativeSearchAccessControl,
  NativeSearchAction,
} from '../../../../hooks/useNativeSearchAccessControl';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import AccountSettings from '../accountSettings/AccountSettings';

export interface AccountListProps {
  accountType:
    | 'DICE_SEARCH'
    | 'CAREER_BUILDER'
    | 'CAREER_BUILDER_EDGE'
    | 'TECH_FETCH'
    | 'CV_LIBRARY'
    | 'MONSTER'
    | 'JOB_SERVE'
    | 'CW_JOBS'
    | 'JOB_SITE';
  tableData: IJobBoardDTO[];
  dataUpdater(data: IJobBoardDTO[]);
  latestData(): void;
  handleQuotaRefresh?: (jobBoard: IJobBoardDTO) => Promise<void>;
}
const AccountList: FC<AccountListProps> = ({
  accountType,
  tableData,
  dataUpdater,
  latestData,
  handleQuotaRefresh,
}) => {
  const admin = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { access } = useNativeSearchAccessControl([
    NativeSearchAction.VIEW_NATIVE_SEARCH_ACCOUNTS,
    NativeSearchAction.MANAGE_NATIVE_SEARCH_ACCOUNTS,
    NativeSearchAction.DELETE_JOB_BOARD_ACCOUNTS,
  ]);
  const [view, manage, deleteJobBoard] = access;
  const [indexValue, setIndexValue] = useState<number>(-1);
  const [showDrawer, setShowDrawer] = useState(false);
  const closeDrawer = () => {
    setShowDrawer(false);
    setIndexValue(-1);
  };
  const columns = [
    {
      Header: 'Account Name',
      accessor: 'accountName',
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
    },
    {
      Header: 'Quota',
      accessor: 'quota',
    },
    {
      Header: 'Shared with',
      accessor: 'sharedWith',
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: (cell: Cell) => (
        <Stack spacing={1} direction="row">
          {handleQuotaRefresh && (
            <Tooltip title="Refresh Quota">
              <IconButton onClick={() => handleQuotaRefresh(tableData[cell.row.index])}>
                <Cached />
              </IconButton>
            </Tooltip>
          )}
          {(admin.owns || admin.roles?.indexOf('ROLE_ADMIN') != -1 || view || manage) && (
            <Tooltip title="Refresh Quota">
              <IconButton
                onClick={() => {
                  setIndexValue(cell.row.index), setShowDrawer(true);
                }}
              >
                <WsIconEdit />
              </IconButton>
            </Tooltip>
          )}

          {(admin.owns || admin.roles?.indexOf('ROLE_ADMIN') != -1 || deleteJobBoard) && (
            <Tooltip title="Refresh Quota">
              <IconButton onClick={() => onDeleteHandler(tableData[cell.row.index].id)}>
                <WsIconDelete />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];
  const onDeleteHandler = async (id: string) => {
    try {
      await deleteJobBoardAccounts(id);
      setNotification(success('Account Deleted Successfully.'));
      latestData();
    } catch (error) {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };
  const updaterFunction = (data, index) => {
    const tempData = tableData;
    tempData[index] = data;
    dataUpdater([...tempData]);
  };
  const data = useMemo(
    () =>
      tableData.map((list) => ({
        accountName: list.username,
        createdBy: list.createdBy,
        quota: (
          <Label>
            {list.usage?.viewsRemaining}/{list.usage?.quota}
          </Label>
        ),
        sharedWith: list.jobBoardSearchAccountUsers?.length ?? 0,
      })),
    [tableData]
  );
  return (
    <>
      <Box borderRadius={1.5}>
        <WsDataTable columns={columns} data={data} selectable={false} pageable={false} />
      </Box>
      {indexValue != -1 && (
        <Grid marginTop={2} borderRadius={1.5}>
          <AccountSettings
            openDrawer={showDrawer}
            closeDrawer={() => closeDrawer()}
            accountType={accountType}
            tableData={tableData[indexValue]}
            dataIndex={indexValue}
            country=""
            dataUpdater={(data, index) => updaterFunction(data, index)}
          />
        </Grid>
      )}
    </>
  );
};
export default AccountList;
